import { useQuery } from "@tanstack/react-query";
import {
  Activity,
  AlertTriangle,
  BadgeCheck,
  CalendarDays,
  CalendarRange,
  CheckCircle2,
  Clock,
  IndianRupee,
  LifeBuoy,
  MapPin,
  ShieldCheck,
  Sprout,
  Truck,
  XCircle,
} from "lucide-react";
import * as React from "react";
import { Link } from "react-router-dom";

import { analytics, kyc, tickets } from "@/api/endpoints";
import type { PeriodMetrics, RegionRow } from "@/api/types";
import { useAuth } from "@/auth/AuthProvider";
import {
  ProportionBar,
  RankedBars,
  SERIES,
  STATUS_COLOR,
  StatTile,
  StatusDonut,
  TrendChart,
  type Datum,
} from "@/components/charts";
import {
  DateRangeFilter,
  rangeForPreset,
  type DateRange,
} from "@/components/DateRangeFilter";
import { PageHeader } from "@/components/PageHeader";
import { Badge, toneFor } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { EmptyState, ErrorState, Skeleton } from "@/components/ui/feedback";
import { Select } from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatCurrency, formatNumber, humanize, relativeTime } from "@/lib/utils";

/** Colour per booking status — a fixed slot assignment, never cycled. */
const STATUS_COLORS: Record<string, string> = {
  REQUESTED: SERIES[1],
  QUOTED: SERIES[3],
  CUSTOMER_CONFIRMED: SERIES[4],
  PROVIDER_ACCEPTED: SERIES[5],
  START_OTP_ISSUED: SERIES[2],
  CONFIRMED: SERIES[1],
  IN_PROGRESS: SERIES[0],
  COMPLETED: STATUS_COLOR.good,
  CANCELLED: STATUS_COLOR.critical,
};

export default function Dashboard() {
  const { me, can } = useAuth();

  const [preset, setPreset] = React.useState("30");
  const [range, setRange] = React.useState<DateRange>(() => rangeForPreset("30"));
  const [state, setState] = React.useState("");
  const [city, setCity] = React.useState("");

  function changePreset(next: string) {
    setPreset(next);
    if (next !== "custom") setRange(rangeForPreset(next));
  }

  const canOps = can("booking.read");

  const regionOptions = useQuery({
    queryKey: ["analytics", "regions"],
    queryFn: analytics.regions,
    enabled: canOps,
  });

  const overview = useQuery({
    queryKey: ["analytics", "overview", { range, state, city }],
    queryFn: () =>
      analytics.overview({
        date_from: range.from,
        date_to: range.to,
        state: state || undefined,
        city: city || undefined,
        region_limit: 10,
      }),
    enabled: canOps,
  });

  const ticketStats = useQuery({
    queryKey: ["ticket-stats"],
    queryFn: tickets.stats,
    enabled: can("ticket.read"),
  });

  const overdueTickets = useQuery({
    queryKey: ["tickets", "overdue-preview"],
    queryFn: () => tickets.list({ overdue: true, page_size: 5 }),
    enabled: can("ticket.read"),
  });

  const kycQueue = useQuery({
    queryKey: ["kyc", "queue-preview"],
    queryFn: () => kyc.queue({ page_size: 5 }),
    enabled: can("kyc.read"),
  });

  if (!canOps && !can("ticket.read") && !can("kyc.read")) {
    return (
      <>
        <PageHeader title="Dashboard" />
        <EmptyState
          title="Nothing assigned to your role yet"
          description="Your account doesn't have dashboard permissions. Ask an administrator."
        />
      </>
    );
  }

  const data = overview.data;
  const loading = overview.isLoading;

  const statusData: Datum[] = data
    ? Object.entries(data.status_mix)
        .filter(([, count]) => count > 0)
        .map(([status, count]) => ({
          label: humanize(status),
          value: count,
          color: STATUS_COLORS[status] ?? SERIES[0],
        }))
    : [];

  const categoryData: Datum[] = data
    ? data.by_category.map((c) => ({ label: c.category, value: c.orders }))
    : [];

  const regionChartData: Datum[] = data
    ? data.by_region.slice(0, 8).map((r) => ({ label: r.region, value: r.orders }))
    : [];

  return (
    <>
      <PageHeader
        title={`Good day, ${me?.full_name?.split(" ")[0] ?? "there"}`}
        description="Daily operations across orders, farmers, providers and support."
      />

      {/* ── Filters ─────────────────────────────────────────────────── */}
      {canOps && (
        <Card className="mb-4">
          <CardContent className="flex flex-wrap items-end gap-4 p-4">
            <DateRangeFilter
              preset={preset}
              range={range}
              onPresetChange={changePreset}
              onRangeChange={setRange}
            />
            <div className="w-44">
              <label className="mb-1.5 block text-xs font-medium">State</label>
              <Select
                value={state}
                onValueChange={setState}
                options={(regionOptions.data?.states ?? []).map((s) => ({
                  label: s,
                  value: s,
                }))}
                placeholder="All states"
                aria-label="Filter by state"
              />
            </div>
            <div className="w-44">
              <label className="mb-1.5 block text-xs font-medium">
                District / city
              </label>
              <Select
                value={city}
                onValueChange={setCity}
                options={(regionOptions.data?.cities ?? []).map((c) => ({
                  label: c,
                  value: c,
                }))}
                placeholder="All districts"
                aria-label="Filter by district or city"
              />
            </div>
            {(state || city) && (
              <button
                type="button"
                onClick={() => {
                  setState("");
                  setCity("");
                }}
                className="pb-2 text-xs font-medium text-primary underline underline-offset-4"
              >
                Clear region
              </button>
            )}
          </CardContent>
        </Card>
      )}

      {overview.isError ? (
        <ErrorState error={overview.error} onRetry={() => overview.refetch()} />
      ) : (
        <div className="space-y-5">
          {/* ── Today at a glance ────────────────────────────────────── */}
          {canOps && (
            <section>
              <h2 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Today
              </h2>
              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                <PeriodTiles metrics={data?.periods.today} loading={loading} />
              </div>
              {data && (
                <p className="mt-2 text-xs text-muted-foreground">
                  Yesterday: {formatNumber(data.periods.yesterday.placed)} placed ·{" "}
                  {formatNumber(data.periods.yesterday.completed)} completed ·{" "}
                  {formatNumber(data.periods.yesterday.cancelled)} cancelled
                </p>
              )}
            </section>
          )}

          {/* ── Month & year ─────────────────────────────────────────── */}
          {canOps && (
            <section>
              <h2 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                This month and year
              </h2>
              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                {loading ? (
                  <>
                    <Skeleton className="h-[92px]" />
                    <Skeleton className="h-[92px]" />
                    <Skeleton className="h-[92px]" />
                    <Skeleton className="h-[92px]" />
                  </>
                ) : (
                  <>
                    <StatTile
                      label="Orders this month"
                      value={data?.periods.month.placed ?? 0}
                      hint={`${formatNumber(data?.periods.month.completed ?? 0)} completed`}
                      icon={CalendarDays}
                    />
                    <StatTile
                      label="Revenue this month"
                      value={formatCurrency(data?.periods.month.revenue ?? 0)}
                      hint="From completed orders"
                      tone="good"
                      icon={IndianRupee}
                    />
                    <StatTile
                      label="Orders this year"
                      value={data?.periods.year.placed ?? 0}
                      hint={`${formatNumber(data?.periods.year.completed ?? 0)} completed`}
                      icon={CalendarRange}
                    />
                    <StatTile
                      label="Revenue this year"
                      value={formatCurrency(data?.periods.year.revenue ?? 0)}
                      hint="From completed orders"
                      tone="good"
                      icon={IndianRupee}
                    />
                  </>
                )}
              </div>
            </section>
          )}

          {/* ── Growth ───────────────────────────────────────────────── */}
          {canOps && (
            <section>
              <h2 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Growth and marketplace
              </h2>
              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                {loading ? (
                  <>
                    <Skeleton className="h-[92px]" />
                    <Skeleton className="h-[92px]" />
                    <Skeleton className="h-[92px]" />
                    <Skeleton className="h-[92px]" />
                  </>
                ) : (
                  <>
                    <StatTile
                      label="New farmers today"
                      value={data?.periods.today.new_users.farmers ?? 0}
                      hint={`${formatNumber(data?.periods.month.new_users.farmers ?? 0)} this month · ${formatNumber(data?.lifetime.farmers ?? 0)} total`}
                      icon={Sprout}
                    />
                    <StatTile
                      label="New providers today"
                      value={data?.periods.today.new_users.providers ?? 0}
                      hint={`${formatNumber(data?.periods.month.new_users.providers ?? 0)} this month · ${formatNumber(data?.lifetime.providers ?? 0)} total`}
                      icon={Truck}
                    />
                    <StatTile
                      label="Verified providers"
                      value={data?.lifetime.verified_providers ?? 0}
                      hint={`${formatNumber(data?.lifetime.online_providers ?? 0)} online now`}
                      tone="good"
                      icon={BadgeCheck}
                    />
                    <StatTile
                      label="Orders in flight"
                      value={data?.lifetime.active ?? 0}
                      hint={`${formatNumber(data?.lifetime.open_tenders ?? 0)} open tenders`}
                      tone="warning"
                      icon={Activity}
                    />
                  </>
                )}
              </div>
            </section>
          )}

          {/* ── Trend ────────────────────────────────────────────────── */}
          {canOps && (
            <Card>
              <CardHeader>
                <CardTitle>Order activity</CardTitle>
                <CardDescription>
                  {data
                    ? `${data.range.from} to ${data.range.to}${
                        data.filters.city ? ` · ${data.filters.city}` : ""
                      }`
                    : "Loading…"}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <Skeleton className="h-[260px]" />
                ) : (
                  <TrendChart data={data?.trend ?? []} />
                )}
              </CardContent>
            </Card>
          )}

          {/* ── Range totals + status mix ────────────────────────────── */}
          {canOps && (
            <div className="grid gap-4 lg:grid-cols-2">
              <Card>
                <CardHeader>
                  <CardTitle>Selected period</CardTitle>
                  <CardDescription>
                    Totals for the date range above, not the calendar month.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {loading ? (
                    <Skeleton className="h-[180px]" />
                  ) : (
                    <RangeSummary metrics={data?.range_totals} />
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Order status mix</CardTitle>
                  <CardDescription>
                    Orders placed in the selected period, by current status.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {loading ? (
                    <Skeleton className="h-[200px]" />
                  ) : (
                    <StatusDonut data={statusData} />
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* ── District league table ────────────────────────────────── */}
          {canOps && (
            <div className="grid gap-4 lg:grid-cols-2">
              <Card>
                <CardHeader>
                  <CardTitle>Orders by district</CardTitle>
                  <CardDescription>
                    Grouped by the city recorded on the booking.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {loading ? (
                    <Skeleton className="h-[220px]" />
                  ) : (
                    <RankedBars
                      data={regionChartData}
                      emptyLabel="No orders in this period"
                    />
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Top service categories</CardTitle>
                  <CardDescription>By order volume in the period.</CardDescription>
                </CardHeader>
                <CardContent>
                  {loading ? (
                    <Skeleton className="h-[220px]" />
                  ) : (
                    <RankedBars
                      data={categoryData}
                      emptyLabel="No categorised orders yet"
                    />
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {canOps && data && data.by_region.length > 0 && (
            <Card className="overflow-hidden">
              <CardHeader>
                <CardTitle>District performance</CardTitle>
                <CardDescription>
                  Completion and cancellation split per district.
                </CardDescription>
              </CardHeader>
              <RegionTable rows={data.by_region} />
            </Card>
          )}

          {/* ── Support ──────────────────────────────────────────────── */}
          {(can("ticket.read") || can("kyc.read")) && (
            <section>
              <h2 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Support and verification
              </h2>
              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                {can("ticket.read") && (
                  <>
                    <StatTile
                      label="Open tickets"
                      value={
                        (ticketStats.data?.by_status.OPEN ?? 0) +
                        (ticketStats.data?.by_status.IN_PROGRESS ?? 0) +
                        (ticketStats.data?.by_status.PENDING_CUSTOMER ?? 0)
                      }
                      hint={`${formatNumber(ticketStats.data?.unassigned ?? 0)} unassigned`}
                      icon={LifeBuoy}
                    />
                    <StatTile
                      label="Past SLA"
                      value={ticketStats.data?.overdue ?? 0}
                      hint={
                        (ticketStats.data?.overdue ?? 0) > 0
                          ? "Needs attention now"
                          : "All on time"
                      }
                      tone={
                        (ticketStats.data?.overdue ?? 0) > 0 ? "critical" : "good"
                      }
                      icon={AlertTriangle}
                    />
                  </>
                )}
                {can("kyc.read") && (
                  <StatTile
                    label="Awaiting KYC"
                    value={kycQueue.data?.total ?? 0}
                    hint="Providers blocked until reviewed"
                    tone={(kycQueue.data?.total ?? 0) > 0 ? "warning" : "good"}
                    icon={ShieldCheck}
                  />
                )}
                {canOps && (
                  <StatTile
                    label="Cancelled all time"
                    value={data?.lifetime.cancelled ?? 0}
                    hint={`of ${formatNumber(data?.lifetime.orders ?? 0)} orders`}
                    tone="critical"
                    icon={XCircle}
                  />
                )}
              </div>
            </section>
          )}

          <div className="grid gap-4 lg:grid-cols-2">
            {can("ticket.read") && (
              <Card>
                <CardHeader>
                  <CardTitle>Oldest SLA breaches</CardTitle>
                  <CardDescription>Tickets already past target.</CardDescription>
                </CardHeader>
                <CardContent className="px-0">
                  {overdueTickets.isLoading ? (
                    <div className="space-y-2 px-5">
                      <Skeleton className="h-9" />
                      <Skeleton className="h-9" />
                    </div>
                  ) : overdueTickets.data?.items.length ? (
                    <ul className="divide-y divide-border">
                      {overdueTickets.data.items.map((t) => (
                        <li key={t.id}>
                          <Link
                            to={`/tickets/${t.id}`}
                            className="flex items-center gap-3 px-5 py-2.5 hover:bg-muted/60"
                          >
                            <span className="tabular shrink-0 text-xs text-muted-foreground">
                              {t.reference}
                            </span>
                            <span className="min-w-0 flex-1 truncate text-sm">
                              {t.subject}
                            </span>
                            <Badge variant={toneFor(t.priority)}>
                              {humanize(t.priority)}
                            </Badge>
                            <span className="hidden shrink-0 text-xs text-muted-foreground sm:inline">
                              {relativeTime(t.sla_due_at)}
                            </span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <EmptyState
                      icon={Clock}
                      title="Nothing past SLA"
                      description="Every open ticket is inside its target."
                    />
                  )}
                </CardContent>
              </Card>
            )}

            {can("kyc.read") && (
              <Card>
                <CardHeader>
                  <CardTitle>Providers awaiting review</CardTitle>
                  <CardDescription>
                    They cannot take jobs until verified.
                  </CardDescription>
                </CardHeader>
                <CardContent className="px-0">
                  {kycQueue.isLoading ? (
                    <div className="space-y-2 px-5">
                      <Skeleton className="h-9" />
                    </div>
                  ) : kycQueue.data?.items.length ? (
                    <ul className="divide-y divide-border">
                      {kycQueue.data.items.map((p) => (
                        <li key={p.id} className="flex items-center gap-3 px-5 py-2.5">
                          <Truck className="size-4 shrink-0 text-muted-foreground" />
                          <span className="min-w-0 flex-1 truncate text-sm">
                            {p.business_name}
                          </span>
                          <span className="hidden shrink-0 text-xs text-muted-foreground sm:inline">
                            {p.city ?? "—"}
                          </span>
                          <Badge variant={toneFor(p.verification_status)}>
                            {humanize(p.verification_status)}
                          </Badge>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <EmptyState
                      icon={ShieldCheck}
                      title="Review queue is clear"
                      description="No providers waiting on verification."
                    />
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      )}
    </>
  );
}

function PeriodTiles({
  metrics,
  loading,
}: {
  metrics?: PeriodMetrics;
  loading: boolean;
}) {
  if (loading) {
    return (
      <>
        <Skeleton className="h-[92px]" />
        <Skeleton className="h-[92px]" />
        <Skeleton className="h-[92px]" />
        <Skeleton className="h-[92px]" />
      </>
    );
  }
  return (
    <>
      <StatTile
        label="Orders placed today"
        value={metrics?.placed ?? 0}
        hint="New bookings created"
        icon={CalendarDays}
      />
      <StatTile
        label="Completed today"
        value={metrics?.completed ?? 0}
        hint={
          metrics?.completion_rate != null
            ? `${metrics.completion_rate}% of concluded orders`
            : "Nothing concluded yet"
        }
        tone="good"
        icon={CheckCircle2}
      />
      <StatTile
        label="Cancelled today"
        value={metrics?.cancelled ?? 0}
        hint="Includes provider and customer cancellations"
        tone={(metrics?.cancelled ?? 0) > 0 ? "critical" : "good"}
        icon={XCircle}
      />
      <StatTile
        label="Revenue today"
        value={formatCurrency(metrics?.revenue ?? 0)}
        hint="From orders completed today"
        tone="good"
        icon={IndianRupee}
      />
    </>
  );
}

function RangeSummary({ metrics }: { metrics?: PeriodMetrics }) {
  if (!metrics) return null;

  const concluded = metrics.completed + metrics.cancelled;

  return (
    <div className="space-y-4">
      <dl className="grid grid-cols-2 gap-4">
        <div>
          <dt className="text-xs text-muted-foreground">Orders placed</dt>
          <dd className="text-2xl font-semibold">{formatNumber(metrics.placed)}</dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">Revenue</dt>
          <dd className="text-2xl font-semibold">
            {formatCurrency(metrics.revenue)}
          </dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">New farmers</dt>
          <dd className="text-2xl font-semibold">
            {formatNumber(metrics.new_users.farmers)}
          </dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">New providers</dt>
          <dd className="text-2xl font-semibold">
            {formatNumber(metrics.new_users.providers)}
          </dd>
        </div>
      </dl>

      {concluded > 0 && (
        <div>
          <ProportionBar
            segments={[
              {
                label: "Completed",
                value: metrics.completed,
                color: STATUS_COLOR.good,
              },
              {
                label: "Cancelled",
                value: metrics.cancelled,
                color: STATUS_COLOR.critical,
              },
            ]}
          />
          <div className="mt-2 flex flex-wrap gap-4 text-xs">
            <span className="flex items-center gap-1.5">
              <CheckCircle2
                className="size-3.5"
                style={{ color: STATUS_COLOR.good }}
              />
              <span className="text-muted-foreground">Completed</span>
              <span className="tabular font-medium">
                {formatNumber(metrics.completed)}
              </span>
            </span>
            <span className="flex items-center gap-1.5">
              <XCircle
                className="size-3.5"
                style={{ color: STATUS_COLOR.critical }}
              />
              <span className="text-muted-foreground">Cancelled</span>
              <span className="tabular font-medium">
                {formatNumber(metrics.cancelled)}
              </span>
            </span>
            {metrics.completion_rate != null && (
              <span className="text-muted-foreground">
                Completion rate{" "}
                <span className="tabular font-medium text-foreground">
                  {metrics.completion_rate}%
                </span>
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function RegionTable({ rows }: { rows: RegionRow[] }) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>District / city</TableHead>
          <TableHead className="text-right">Orders</TableHead>
          <TableHead className="text-right">Completed</TableHead>
          <TableHead className="text-right">Cancelled</TableHead>
          <TableHead className="text-right">Value</TableHead>
          <TableHead className="w-32">Split</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((r) => (
          <TableRow key={r.region}>
            <TableCell className="font-medium">
              <span className="flex items-center gap-1.5">
                <MapPin className="size-3.5 text-muted-foreground" />
                {r.region}
              </span>
            </TableCell>
            <TableCell className="tabular text-right">
              {formatNumber(r.orders)}
            </TableCell>
            <TableCell
              className="tabular text-right"
              style={{ color: STATUS_COLOR.good }}
            >
              {formatNumber(r.completed)}
            </TableCell>
            <TableCell
              className="tabular text-right"
              style={{ color: STATUS_COLOR.critical }}
            >
              {formatNumber(r.cancelled)}
            </TableCell>
            <TableCell className="tabular text-right">
              {formatCurrency(r.value)}
            </TableCell>
            <TableCell>
              {r.completed + r.cancelled > 0 ? (
                <ProportionBar
                  segments={[
                    {
                      label: "Completed",
                      value: r.completed,
                      color: STATUS_COLOR.good,
                    },
                    {
                      label: "Cancelled",
                      value: r.cancelled,
                      color: STATUS_COLOR.critical,
                    },
                  ]}
                />
              ) : (
                <span className="text-xs text-muted-foreground">In flight</span>
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
